import { useCallback, useEffect, useState } from 'react'

import {
  getVaultPermission,
  pickObsidianVault,
  supportsObsidianFolderAccess,
  writeMaterialToObsidian,
  type LocalDirectoryHandle,
} from '../lib/obsidianVault'
import {
  loadVaultDirectoryHandle,
  saveVaultDirectoryHandle,
} from '../lib/materialStore'
import type {
  LocalMaterialRecord,
  VaultConnectionStatus,
} from '../types'

export function useObsidianVault() {
  const [handle, setHandle] = useState<LocalDirectoryHandle | null>(null)
  const [status, setStatus] = useState<VaultConnectionStatus>('checking')
  const [message, setMessage] = useState('正在检查 Obsidian 知识库连接…')

  useEffect(() => {
    let isActive = true

    if (!supportsObsidianFolderAccess()) {
      setStatus('unsupported')
      setMessage('当前浏览器不支持直接写入本地文件夹，可继续使用浏览器本机存储。')
      return undefined
    }

    loadVaultDirectoryHandle<LocalDirectoryHandle>()
      .then(async (storedHandle) => {
        if (!isActive) {
          return
        }

        if (!storedHandle) {
          setStatus('disconnected')
          setMessage('尚未连接 Obsidian 知识库文件夹。')
          return
        }

        setHandle(storedHandle)
        const permission = await getVaultPermission(storedHandle, false)

        if (!isActive) {
          return
        }

        if (permission === 'granted') {
          setStatus('connected')
          setMessage(`已连接本机知识库：${storedHandle.name}`)
        } else {
          setStatus('permission-needed')
          setMessage('已记住知识库文件夹，点击“重新授权”即可写入。')
        }
      })
      .catch(() => {
        if (isActive) {
          setStatus('disconnected')
          setMessage('未读取到已保存的知识库连接。')
        }
      })

    return () => {
      isActive = false
    }
  }, [])

  const connect = useCallback(async () => {
    if (!supportsObsidianFolderAccess()) {
      setStatus('unsupported')
      setMessage('请使用最新版 Chrome 或 Edge 连接本地知识库。')
      return false
    }

    setStatus('connecting')
    setMessage('正在请求本地知识库权限…')

    try {
      if (handle) {
        const permission = await getVaultPermission(handle, true)

        if (permission === 'granted') {
          setStatus('connected')
          setMessage(`已连接本机知识库：${handle.name}`)
          return true
        }
      }

      const nextHandle = await pickObsidianVault()
      await saveVaultDirectoryHandle(nextHandle)
      setHandle(nextHandle)
      setStatus('connected')
      setMessage(`已连接本机知识库：${nextHandle.name}`)
      return true
    } catch (error) {
      const nextMessage =
        error instanceof Error && error.name !== 'AbortError'
          ? error.message
          : '已取消选择知识库文件夹。'
      setStatus(handle ? 'permission-needed' : 'disconnected')
      setMessage(nextMessage)
      return false
    }
  }, [handle])

  const writeMaterial = useCallback(
    async (record: LocalMaterialRecord) => {
      if (!handle || status !== 'connected') {
        return null
      }

      return writeMaterialToObsidian(handle, record)
    },
    [handle, status],
  )

  return {
    connect,
    isConnected: status === 'connected',
    message,
    status,
    vaultName: handle?.name ?? '知识库',
    writeMaterial,
  }
}
